import './ClientAuthorisationToReleaseHealthInfo.scss';
import BackComponent from "../../../shared/components/back/BackComponent";
import {CommonService} from "../../../shared/services";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import * as Yup from "yup";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormControlLabelComponent from "../../../shared/components/form-control-label/FormControlLabelComponent";
import FormikInputComponent from "../../../shared/components/form-controls/formik-input/FormikInputComponent";
import {ImageConfig, Misc, Patterns} from "../../../constants";
import FormikPhoneInputComponent from "../../../shared/components/formik-phone-input/FormikPhoneInputComponent";
import FormikDatePickerComponent
    from "../../../shared/components/form-controls/formik-date-picker/FormikDatePickerComponent";
import HorizontalLineComponent
    from "../../../shared/components/horizontal-line/horizontal-line/HorizontalLineComponent";
import FormikTextAreaComponent from "../../../shared/components/form-controls/formik-text-area/FormikTextAreaComponent";
import CheckBoxComponent from "../../../shared/components/form-controls/check-box/CheckBoxComponent";
import _ from "lodash";
import ButtonComponent from "../../../shared/components/button/ButtonComponent";
import FormikCheckBoxComponent from "../../../shared/components/form-controls/formik-check-box/FormikCheckBoxComponent";
import ErrorComponent from "../../../shared/components/error/ErrorComponent";
import ESignApprovalComponent from "../../../shared/components/e-sign-approval/ESignApprovalComponent";
import LinkComponent from "../../../shared/components/link/LinkComponent";
import ModalComponent from "../../../shared/components/modal/ModalComponent";
import SignaturePopupComponent from "../../../shared/components/signature-popup/SignaturePopupComponent";
import FormDebuggerComponent from "../../../shared/components/form-debugger/FormDebuggerComponent";
import CardComponent from "../../../shared/components/card/CardComponent";
import FormikAutoCompleteComponent
    from "../../../shared/components/form-controls/formik-auto-complete/FormikAutoCompleteComponent";
import moment from "moment-timezone";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import {getAuthCasesList} from "../../../store/actions/dashboard.action";
import {IAPIResponseType} from "../../../shared/models/api.model";
import LoaderComponent from "../../../shared/components/loader/LoaderComponent";

interface ClientAuthorisationToReleaseHealthInfoProps {

}

const AuthorizationToReleaseMedicalHealthInformationInitialValues: any = {
    patient_name: '',
    patient_ssn: '',
    patient_phone: '',
    patient_dob: '',
    patient_address: {
        address_line: '',
        zip_code: '',
        state: '',
        city: '',
        country: ''
    },
    authorized_records: [],
    client_name: '',
    date: new Date(),
    other_information: '',
    signature: '',
    is_authorized_representative: false,
    agreed_to_terms: false,
    representative_name: '',
    information_accessed_by: [
        {
            name: '',
            phone: '',
            address: {
                address_line: '',
                zip_code: '',
                state: '',
                city: '',
                country: ''
            }
        }
    ],
    records: []
}

const authorizationToReleaseMedicalHealthInformationValidationSchema = Yup.object({
    patient_name: Yup.string().required('Name is required'),
    patient_ssn: Yup.string().matches(/^\d{9}$/, 'SSN is not valid').nullable(),
    patient_phone: Yup.string().required('Phone is required'),
    patient_dob: Yup.string().required('Date is required'),
    date: Yup.string().nullable(),
    patient_address: Yup.object({
        address_line: Yup.string().required('Address is required'),
        state: Yup.string().required('State is required'),
        city: Yup.string().required('City is required'),
        country: Yup.string().required('Country is required'),
        zip_code: Yup.string().required('Zip Code is required'),
    }),
    information_accessed_by: Yup.array().of(
        Yup.object().shape({
            name: Yup.string().required('Name is required'),
            phone: Yup.string().required('Phone Number is required'),
            address: Yup.object().shape({
                address_line: Yup.string().required('Address Line is required'),
                zip_code: Yup.string()
                    .min(5, 'Zip code must be at least 5 characters')
                    .max(10, 'Zip code must be at most 10 characters')
                    .required('Zip Code is required'),
                state: Yup.string().required('State is required'),
                city: Yup.string().required('City is required'),
                country: Yup.string().required('Country is required'),
            }),
        })
    ).min(1),
    is_authorized_representative: Yup.boolean(),
    agreed_to_terms: Yup.boolean().oneOf([true], 'Please acknowledge to the terms and condition'),
    representative_name: Yup.string().nullable().when("is_authorized_representative", {
        is: true,
        then: Yup.string().nullable().required('Authorized Representative Name is required')
    }),
    signature: Yup.string().required()
})

const ClientAuthorisationToReleaseHealthInfo = (props: ClientAuthorisationToReleaseHealthInfoProps) => {

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [authorizationToReleaseMedicalHealthInformationFormValues, setAuthorizationToReleaseMedicalInformationFormValues] = useState<any>(_.cloneDeep(AuthorizationToReleaseMedicalHealthInformationInitialValues));
    const formikRef = useRef<any>(null);
    const [authorizedRecords, setAuthorizedRecords] = useState<any>([]);
    const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState<boolean>(false);
    const {authAllCasesList, isAuthCasesListLoading,isAuthCasesListLoaded} = useSelector((state: IRootReducerState) => state.dashboard);
    const {appointmentId} = useParams();
    const [isDetailsLoading, setDetailsLoading] = useState<boolean>(false);
    const [getDetails, setGetDetails] = useState<any>([]);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const mode = searchParams.get("mode");
    const {clientBasicDetails} = useSelector((state: IRootReducerState) => state.client);

    const onSubmit = useCallback((values: any, {setSubmitting, setErrors}: FormikHelpers<any>) => {
        setSubmitting(true);
        const payload = {
            ...values,
            authorized_records: authorizedRecords,
            record_id: getDetails?.record_id,
            record_type: getDetails?.record_type,
            mode: mode,
        }
        payload.records = values?.records?.map((record: any) => {
            return {
                record_id: record._id,
                record_type: record.record_type,
            }
        })
        let apiCall;
        if (appointmentId && (mode === "edit" || mode === 'renew')) {
            apiCall = CommonService._dashboard.EditAuthorisationToReleaseHealthInfo(appointmentId, payload)
        } else {
            apiCall = CommonService._dashboard.AddAuthorisationToReleaseHealthInfo(payload)
        }
        apiCall.then((response: IAPIResponseType<any>) => {
            CommonService._alert.showToast(response[Misc.API_RESPONSE_MESSAGE_KEY], "success");
            navigate(CommonService._routeConfig.ClientResource());
            setSubmitting(false);
        })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })

    }, [getDetails, authorizedRecords, appointmentId, mode, navigate]);


    const handleSignaturePopupClose = useCallback(() => {
        setIsSignaturePopupOpen(false);
    }, []);

    useEffect(() => {
        dispatch(getAuthCasesList());
    }, [dispatch]);

    const getAuthHealthDetails = useCallback((appointmentId: string) => {
        setDetailsLoading(true);
        CommonService._dashboard.GetAuthorisationToReleaseHealthInfo(appointmentId)
            .then((response: IAPIResponseType<any>) => {
                setDetailsLoading(false);
                setGetDetails(response.data)
            }).catch((error: any) => {
            setDetailsLoading(false);
            setGetDetails([]);
        })
    }, []);

    useEffect(() => {
        if (appointmentId) {
            getAuthHealthDetails(appointmentId);
        }
    }, [getAuthHealthDetails, appointmentId]);

    useEffect(() => {
        if (mode !== 'edit' && mode !== 'renew') {
            setAuthorizationToReleaseMedicalInformationFormValues((prevState: any) => ({
                ...prevState,
                patient_name: clientBasicDetails?.alias_first_name ? clientBasicDetails?.alias_first_name + ' ' + clientBasicDetails?.alias_last_name : clientBasicDetails?.first_name + ' ' + clientBasicDetails?.last_name,
                patient_ssn: clientBasicDetails?.ssn || '',
                patient_phone: clientBasicDetails?.primary_contact_info?.phone || '',
                patient_dob: clientBasicDetails?.dob || '',
                patient_address: {
                    address_line: clientBasicDetails?.address?.address_line || '',
                    zip_code: clientBasicDetails?.address?.zip_code || '',
                    state: clientBasicDetails?.address?.state || '',
                    city: clientBasicDetails?.address?.city || '',
                    country: clientBasicDetails?.address?.country || '',
                },
                client_name: clientBasicDetails?.first_name + ' ' + clientBasicDetails?.last_name,
            }))
        }
    }, [mode, clientBasicDetails]);

    useEffect(() => {
        if (appointmentId && getDetails && (mode === 'edit' || mode === 'renew')) {
            setAuthorizationToReleaseMedicalInformationFormValues((prevState: any) => ({
                ...prevState,
                patient_name: getDetails?.patient_name,
                patient_ssn: getDetails?.patient_ssn || '',
                patient_phone: getDetails?.patient_phone || '',
                patient_dob: getDetails?.patient_dob || '',
                patient_address: {
                    address_line: getDetails?.patient_address?.address_line || '',
                    zip_code: getDetails?.patient_address?.zip_code || '',
                    state: getDetails?.patient_address?.state || '',
                    city: getDetails?.patient_address?.city || '',
                    country: getDetails?.patient_address?.country || '',
                },
                information_accessed_by: getDetails?.information_accessed_by,
                case_details: getDetails?.case_details,
                other_information: getDetails?.other_information || '',
                signature: getDetails?.signature || '',
                is_authorized_representative: getDetails?.is_authorized_representative || false,
                agreed_to_terms: getDetails?.agreed_to_terms || false,
                representative_name: getDetails?.representative_name || '',
                client_name: clientBasicDetails?.first_name + ' ' + clientBasicDetails?.last_name,


            }))
            setAuthorizedRecords(getDetails?.authorized_records || []);

        }
    }, [clientBasicDetails, mode, appointmentId, getDetails]);
    console.log('authAllCasesList',authAllCasesList)


    return (
        <div className="client-authorisation-to-release-health-info">
            {
                isDetailsLoading && <LoaderComponent/>
            }
            {
                !isDetailsLoading && <>
                    <div className="list-screen__header">
                        <div className="list-screen__header-title">
                            <BackComponent onClick={() => navigate(CommonService._routeConfig.ClientResource())}/>
                        </div>
                    </div>
                    <CardComponent>
                        <div
                            className={'client-authorisation-to-release-health-info-wrapper'}>
                            <div className="client-authorisation-to-release-health-info-block__title">
                                Authorization to Release Protected Health Information
                            </div>
                            <Formik initialValues={authorizationToReleaseMedicalHealthInformationFormValues}
                                    validateOnChange={false}
                                    validationSchema={authorizationToReleaseMedicalHealthInformationValidationSchema}
                                    validateOnBlur={true}
                                    innerRef={formikRef}
                                    enableReinitialize={true}
                                    validateOnMount={true}
                                    onSubmit={onSubmit}>
                                {({
                                      values,
                                      touched,
                                      errors,
                                      setFieldValue,
                                      validateForm,
                                      isValid,
                                      isSubmitting,
                                      dirty
                                  }) => {
                                    // eslint-disable-next-line react-hooks/rules-of-hooks
                                    useEffect(() => {
                                        validateForm();
                                    }, [validateForm, values]);
                                    return (
                                        <Form className={'t-form'} noValidate={true}>
                                            <FormDebuggerComponent values={values} errors={errors} showDebugger={true}/>
                                            <div className={'t-form-controls'}>
                                                <div className={'authorization-medical-information-text'}>
                                                    This form must be fully completed in order to process your request.
                                                    Your
                                                    Protected Health Information (PHI) will only be disclosed by Kinergy
                                                    Sports Medicine and Performance (hereafter known as KINERGY) to the
                                                    person/entity listed below and will not be shared with any other
                                                    individuals without your consent, unless required or permitted by
                                                    law or
                                                    as outlined in our Notice of Privacy Practices.
                                                </div>
                                                <FormControlLabelComponent className={'patient-information-heading'}
                                                                           label={'Part 1: Client Information'}/>
                                                <div className={'ts-row'}>
                                                    <div className={'ts-col-lg-6'}>
                                                        <Field name={'patient_name'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        id={"full_name_input"}
                                                                        label={'Client Name'}
                                                                        placeholder={'Enter Full Name'}
                                                                        required={true}
                                                                        titleCase={true}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className={'ts-col-lg-6'}>
                                                        <Field name={'patient_ssn'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        id={"social_security_input"}
                                                                        label={'Social Security Number'}
                                                                        required={false}
                                                                        type={'text'}
                                                                        validationPattern={Patterns.NINE_DIGITS_ONLY}
                                                                        titleCase={true}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className={'ts-row'}>
                                                    <div className={'ts-col-lg-6'}>
                                                        <Field name={'patient_phone'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikPhoneInputComponent
                                                                        id={"phone_number_input"}
                                                                        label={'Phone Number'}
                                                                        required={true}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className={'ts-col-lg-6'}>
                                                        <Field name={'patient_dob'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikDatePickerComponent
                                                                        id={"dob_input"}
                                                                        label={'Date of Birth'}
                                                                        placeholder={'Enter your date of birth'}
                                                                        required={true}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className={'ts-row'}>
                                                    <div className={'ts-col-lg-12'}>
                                                        <Field name={'patient_address.address_line'}
                                                               className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        id={"address_input"}
                                                                        label={'Address Line'}
                                                                        placeholder={'Enter your Address'}
                                                                        formikField={field}
                                                                        required={true}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className={'ts-row'}>
                                                    <div className={'ts-col-lg-6'}>
                                                        <Field name={'patient_address.city'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        id={"city_input"}
                                                                        label={'City'}
                                                                        placeholder={'Enter your City'}
                                                                        formikField={field}
                                                                        required={true}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className={'ts-col-lg-6'}>
                                                        <Field name={'patient_address.state'}
                                                               className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        id={"state_input"}
                                                                        label={'State'}
                                                                        placeholder={'Enter your State'}
                                                                        formikField={field}
                                                                        required={true}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className={'ts-row'}>
                                                    <div className={'ts-col-lg-6'}>
                                                        <Field name={'patient_address.zip_code'}
                                                               className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        id={"zip_input"}
                                                                        label={'ZIP Code'}
                                                                        validationPattern={Patterns.FIVE_DIGITS_ONLY}
                                                                        placeholder={'Enter your ZIP Code'}
                                                                        formikField={field}
                                                                        required={true}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className={'ts-col-lg-6'}>
                                                        <Field name={'patient_address.country'}
                                                               className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        id={"state_input"}
                                                                        label={'Country'}
                                                                        placeholder={'Enter your Country'}
                                                                        formikField={field}
                                                                        required={true}
                                                                        fullWidth={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                                <HorizontalLineComponent/>
                                                <FormControlLabelComponent className={'patient-information-heading'}
                                                                           label={'Part 2: Client Authorization'}/>
                                                <div className={'authorization-medical-information-text'}>
                                                    I hereby give authorization to KINERGY SPORTS MEDICINE AND PERFORMANCE
                                                    and
                                                    its staff to disclose and/or release, to the entity listed below, the
                                                    following information in regard to my:
                                                </div>
                                                <div className={'select-cases'}>
                                                    Select Case(s)*
                                                </div>
                                                {(mode !== 'edit' && mode !== 'renew') && <div className={'mrg-bottom-20'}>
                                                    <Field name={'records'}>
                                                        {
                                                            (field: FieldProps) => (
                                                                <FormikAutoCompleteComponent
                                                                    formikField={field}
                                                                    options={authAllCasesList || []}
                                                                    label={''}
                                                                    key={authAllCasesList?.length || 'empty'}
                                                                    isDataLoading={isAuthCasesListLoading}
                                                                    isDataLoaded={isAuthCasesListLoaded}
                                                                    placeholder={'Select Case(s)'}
                                                                    valueExtractor={(option) => option}
                                                                    fullWidth={true}
                                                                    multiple={true}
                                                                    displayWith={(option: any) => {
                                                                        if (option?.record_type === "performance_record") {
                                                                            return `${CommonService.getDateFromTimezone(option?.created_at, moment.tz.guess(), 'DD-MMM-YYYY')} - ${option.program_focus}`;
                                                                        }

                                                                        if (option?.record_type === "medical_record" && option?.injury_details?.length) {
                                                                            const injuries = option?.injury_details
                                                                                .map((injury: any) => `${injury?.body_side} ${injury.body_part_name}`)
                                                                                .join(", ");
                                                                            return `${CommonService.getDateFromTimezone(option?.created_at, moment.tz.guess(), 'DD-MMM-YYYY')} - ${injuries}`;
                                                                        }
                                                                    }}

                                                                />
                                                            )
                                                        }
                                                    </Field>
                                                </div>
                                                }
                                                {
                                                    mode === 'edit' && <>
                                                        <div className="medical-record-header">
                                                            {getDetails?.record_type === 'medical_record' ?
                                                                <span className="medical-info">
                                                            {CommonService.getDateFromTimezone(getDetails?.created_at, moment.tz.guess(), 'DD-MMM-YYYY')} - {
                                                                    Array.from(new Set(
                                                                        getDetails?.case_details?.injury_details
                                                                            ?.map((injury: any) => `${injury.body_part_name} (${injury.body_side})`)
                                                                    )).join(' | ')
                                                                }
                                                            </span> : <span
                                                                    className="medical-info">{<>{CommonService.getDateFromTimezone(getDetails?.created_at, moment.tz.guess(), 'DD-MMM-YYYY')} - {getDetails?.case_details?.program_focus}</>}</span>}
                                                            <div className="dropdown-icon">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16"
                                                                     height="16" viewBox="0 0 24 24" fill="none"
                                                                     stroke="currentColor" stroke-width="2"
                                                                     stroke-linecap="round" stroke-linejoin="round">
                                                                    <polyline points="6 9 12 15 18 9"></polyline>
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    </>
                                                }
                                                <div className={'ts-row'}>
                                                    {CommonService._staticData?.AuthorizedRecords?.map((record: string, index: number) => (

                                                        <div className={'ts-col-6'} key={index + record}>
                                                            {record !== 'other' && <CheckBoxComponent
                                                                label={CommonService.capitalizeFirstLetter(record)}
                                                                color={'accent'}
                                                                onChange={(isChecked: boolean) => {
                                                                    const tempRecords = [...authorizedRecords];
                                                                    if (isChecked) {
                                                                        tempRecords.push(record);
                                                                    } else {
                                                                        _.remove(tempRecords, (item) => item === record);
                                                                    }
                                                                    setAuthorizedRecords(tempRecords)
                                                                    setFieldValue('authorized_records', tempRecords);
                                                                }}
                                                                checked={authorizedRecords?.includes(record)}
                                                            />}
                                                            {(record === 'other') &&
                                                                <CheckBoxComponent
                                                                    color={'accent'}
                                                                    label={CommonService.capitalizeFirstLetter(record) + ' (Please specify below)'}
                                                                    onChange={(isChecked: boolean) => {
                                                                        const tempRecords = [...authorizedRecords];
                                                                        if (isChecked) {
                                                                            tempRecords.push(record);
                                                                        } else {
                                                                            _.remove(tempRecords, (item) => item === record);
                                                                        }
                                                                        setAuthorizedRecords(tempRecords)
                                                                        setFieldValue('authorized_records', tempRecords);
                                                                    }}
                                                                    checked={authorizedRecords?.includes(record)}
                                                                />}
                                                        </div>
                                                    ))}
                                                </div>
                                                {(values?.authorized_records?.includes('other') || authorizedRecords?.includes('other')) &&
                                                    <div className="ts-row" id={"comments-field"}>
                                                        <div className="ts-col-12">
                                                            <Field name={'other_information'}>
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikTextAreaComponent
                                                                            placeholder={"Enter your comments here"}
                                                                            required={values?.option}
                                                                            formikField={field}
                                                                            fullWidth={true}
                                                                            autoFocus={true}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>
                                                        </div>
                                                    </div>}
                                                <div className={'disclose-info'}>
                                                    You may disclose this information to:
                                                </div>
                                                <FieldArray name={'information_accessed_by'}
                                                            render={(arrayHelpers) => (
                                                                <>
                                                                    {values.information_accessed_by && values?.information_accessed_by?.map((item: any, index: any) => {
                                                                        return (
                                                                            <div key={index}>
                                                                                {values?.information_accessed_by?.length > 1 &&
                                                                                    <div
                                                                                        className={'contact-title-and-remove-button-wrapper'}>
                                                                                        <div>Contact {index + 1}:</div>
                                                                                        <ButtonComponent
                                                                                            className={'mrg-left-10'}
                                                                                            color={'error'}
                                                                                            variant={'outlined'}
                                                                                            size={'small'}
                                                                                            prefixIcon={
                                                                                                <ImageConfig.DeleteIcon/>}
                                                                                            onClick={() => {
                                                                                                arrayHelpers.remove(index)
                                                                                            }}
                                                                                        >
                                                                                            Remove
                                                                                        </ButtonComponent>
                                                                                    </div>
                                                                                }
                                                                                <div className={'ts-row'}>
                                                                                    <div className={'ts-col-lg-6'}>
                                                                                        <Field
                                                                                            name={`information_accessed_by[${index}].name`}>
                                                                                            {
                                                                                                (field: FieldProps) => (
                                                                                                    <FormikInputComponent
                                                                                                        formikField={field}
                                                                                                        label={'Full Name/Organization'}
                                                                                                        titleCase={true}
                                                                                                        fullWidth={true}
                                                                                                        required={true}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        </Field>
                                                                                    </div>
                                                                                    <div className={'ts-col-lg-6'}>
                                                                                        <Field
                                                                                            name={`information_accessed_by[${index}].phone`}>
                                                                                            {
                                                                                                (field: FieldProps) => (
                                                                                                    <FormikPhoneInputComponent
                                                                                                        formikField={field}
                                                                                                        label={'Phone Number'}
                                                                                                        fullWidth={true}
                                                                                                        required={true}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        </Field>
                                                                                    </div>
                                                                                </div>
                                                                                <div className={'ts-row'}>
                                                                                    <div className={'ts-col-lg-12'}>
                                                                                        <Field
                                                                                            name={`information_accessed_by[${index}].address.address_line`}>
                                                                                            {
                                                                                                (field: FieldProps) => (
                                                                                                    <FormikInputComponent
                                                                                                        formikField={field}
                                                                                                        label={'Address Line'}
                                                                                                        titleCase={true}
                                                                                                        required={true}
                                                                                                        fullWidth={true}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        </Field>
                                                                                    </div>
                                                                                </div>
                                                                                <div className={'ts-row'}>
                                                                                    <div className={'ts-col-lg-6'}>
                                                                                        <Field
                                                                                            name={`information_accessed_by[${index}].address.city`}>
                                                                                            {
                                                                                                (field: FieldProps) => (
                                                                                                    <FormikInputComponent
                                                                                                        formikField={field}
                                                                                                        label={'City'}
                                                                                                        titleCase={true}
                                                                                                        fullWidth={true}
                                                                                                        required={true}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        </Field>
                                                                                    </div>

                                                                                    <div className={'ts-col-lg-6'}>
                                                                                        <Field
                                                                                            name={`information_accessed_by[${index}].address.state`}>
                                                                                            {
                                                                                                (field: FieldProps) => (
                                                                                                    <FormikInputComponent
                                                                                                        formikField={field}
                                                                                                        label={'State'}
                                                                                                        titleCase={true}
                                                                                                        fullWidth={true}
                                                                                                        required={true}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        </Field>
                                                                                    </div>
                                                                                </div>
                                                                                <div className={'ts-row'}>
                                                                                    <div className={'ts-col-lg-6'}>
                                                                                        <Field
                                                                                            name={`information_accessed_by[${index}].address.zip_code`}>
                                                                                            {
                                                                                                (field: FieldProps) => (
                                                                                                    <FormikInputComponent
                                                                                                        formikField={field}
                                                                                                        validationPattern={Patterns.FIVE_DIGITS_ONLY}
                                                                                                        label={'ZIP Code'}
                                                                                                        titleCase={true}
                                                                                                        required={true}
                                                                                                        fullWidth={true}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        </Field>
                                                                                    </div>
                                                                                    <div className={'ts-col-lg-6'}>
                                                                                        <Field
                                                                                            name={`information_accessed_by[${index}].address.country`}>
                                                                                            {
                                                                                                (field: FieldProps) => (
                                                                                                    <FormikInputComponent
                                                                                                        formikField={field}
                                                                                                        label={'Country'}
                                                                                                        required={true}
                                                                                                        titleCase={true}
                                                                                                        fullWidth={true}
                                                                                                    />
                                                                                                )
                                                                                            }
                                                                                        </Field>
                                                                                    </div>
                                                                                </div>
                                                                                <div className={'ts-row'}>
                                                                                    {
                                                                                        values?.information_accessed_by?.length > 1 &&
                                                                                        <HorizontalLineComponent/>
                                                                                    }
                                                                                    {
                                                                                        (index === values?.information_accessed_by?.length - 1) && values.information_accessed_by.length < 5 &&
                                                                                        <ButtonComponent
                                                                                            className={'mrg-bottom-30'}
                                                                                            prefixIcon={
                                                                                                <ImageConfig.AddIcon/>}
                                                                                            onClick={() => {
                                                                                                arrayHelpers.push({
                                                                                                    name: '',
                                                                                                    phone_number: '',
                                                                                                    address: {
                                                                                                        address_line: '',
                                                                                                        zip: '',
                                                                                                        state: '',
                                                                                                        city: ''
                                                                                                    }
                                                                                                })
                                                                                            }}
                                                                                        >
                                                                                            Add Another Contact
                                                                                        </ButtonComponent>
                                                                                    }
                                                                                </div>

                                                                            </div>
                                                                        )
                                                                    })}
                                                                </>
                                                            )}/>
                                                <div className={'purpose-of-disclosure'}>
                                                    Purpose of Disclosure:
                                                </div>
                                                <div className={'purpose-of-disclosure-text'}>
                                                    I understand that the purpose of this authorization is to allow the
                                                    release
                                                    of relevant pieces of my Protected Health Information (PHI) to the
                                                    person/entity listed above, to facilitate communication, coordination,
                                                    and
                                                    continuity of care. I acknowledge that my information may not be
                                                    disclosed
                                                    to any other individual or entity by KINERGY without my expressed
                                                    consent,
                                                    unless required or permitted by law or as outlined in our Notice of
                                                    Privacy
                                                    Practices.
                                                </div>
                                                <div className={'expiration-date-of-authorization'}>
                                                    Expiration Date of Authorization:
                                                </div>
                                                <div className={'expiration-date-of-authorization-text'}>
                                                    This authorization will remain valid for one (1) year from the date of
                                                    signing.
                                                </div>
                                                <HorizontalLineComponent/>
                                                <div className={'patient-right-heading'}>
                                                    Part 3: Client Acknowledgment and Rights
                                                </div>
                                                <div className={'patient-right-text'}>
                                                    <ul>
                                                        <li className={'pdd-bottom-10'}>I acknowledge that this
                                                            authorization is
                                                            voluntary.
                                                        </li>

                                                        <li className={'pdd-bottom-10'}>I understand that by granting this
                                                            authorization, the recipient of
                                                            my protected health information (PHI) may not be subject to the
                                                            same
                                                            privacy protections under HIPAA, and they may disclose this
                                                            information to others, with or without my consent. Once my
                                                            information is disclosed by KINERGY, it may no longer be
                                                            protected
                                                            by HIPAA or other privacy laws.
                                                        </li>
                                                        <li className={'pdd-bottom-10'}>I understand that I may revoke this
                                                            authorization at any time prior
                                                            to its expiration by submitting written notice of revocation to
                                                            the
                                                            entity providing the information. I understand I cannot revoke
                                                            this
                                                            authorization retroactively for information already released.
                                                        </li>
                                                        <li className={'pdd-bottom-10'}>I understand that I have the right
                                                            to
                                                            access and copy the
                                                            information described on this form upon request.
                                                        </li>
                                                    </ul>
                                                </div>

                                                <div className="ts-row pdd-top-30">
                                                    <div className="ts-col-12">
                                                        <Field name={'agreed_to_terms'}
                                                               className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikCheckBoxComponent
                                                                        label={'I certify that I have read and agree to the statements as written above.'}
                                                                        formikField={field}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className="ts-row">
                                                    <div className="ts-col-12">
                                                        <Field name={'is_authorized_representative'}
                                                               className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikCheckBoxComponent
                                                                        label={'I am an Authorized Representative for the client (ie. Parent, Legal Guardian, Agent, Manager, etc.)'}
                                                                        formikField={field}
                                                                        onChange={() => {
                                                                            setFieldValue('representative_name', '');
                                                                        }}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>

                                                <div className="ts-row mrg-top-20">
                                                    <div className="ts-col-md-12 ts-col-lg-6">
                                                        <Field name={'client_name'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikInputComponent
                                                                        id={"name_input"}
                                                                        label={"Client's Name"}
                                                                        placeholder={"Enter Client's Name"}
                                                                        required={true}
                                                                        disabled={true}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        titleCase={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                    <div className="ts-col-md-12 ts-col-lg-6">
                                                        <Field name={'date'} className="t-form-control">
                                                            {
                                                                (field: FieldProps) => (
                                                                    <FormikDatePickerComponent
                                                                        id={"dob_input"}
                                                                        label={'Date'}
                                                                        placeholder={'Enter Date'}
                                                                        formikField={field}
                                                                        fullWidth={true}
                                                                        disabled={true}
                                                                        readOnly={true}
                                                                    />
                                                                )
                                                            }
                                                        </Field>
                                                    </div>
                                                </div>
                                                {
                                                    values.is_authorized_representative && <div className="ts-row">
                                                        <div className="ts-col-6">
                                                            <Field name={'representative_name'} className="t-form-control">
                                                                {
                                                                    (field: FieldProps) => (
                                                                        <FormikInputComponent
                                                                            label={'Authorized Representative Name'}
                                                                            placeholder={'Enter Representative Name'}
                                                                            required={true}
                                                                            fullWidth={true}
                                                                            formikField={field}
                                                                            titleCase={true}
                                                                        />
                                                                    )
                                                                }
                                                            </Field>
                                                        </div>
                                                    </div>
                                                }
                                                {!(values?.signature && authorizationToReleaseMedicalHealthInformationFormValues?.signature) &&
                                                    <div className="ts-row">
                                                        <div className="ts-col-12">
                                                            <div
                                                                className={"signature-label"}
                                                            >Signature of Client or Authorized Representative
                                                            </div>
                                                            <ButtonComponent
                                                                className={'add-signature-button'}
                                                                onClick={() => {
                                                                    setIsSignaturePopupOpen(true);
                                                                }}
                                                                variant={'outlined'}
                                                                color={'accent'}
                                                            >
                                                                Add Signature
                                                            </ButtonComponent>
                                                        </div>
                                                        {
                                                            (_.get(touched, "signature") && !!(_.get(errors, "signature"))) &&
                                                            <ErrorComponent
                                                                errorText={(_.get(errors, "signature"))}/>
                                                        }
                                                    </div>
                                                }
                                                {(values?.signature) &&
                                                    <div className={'ts-col-6'}>
                                                        <ESignApprovalComponent isSigned={true}
                                                                                signature_url={values?.signature}
                                                        />
                                                        <div className={'signature-remove-button'}>
                                                            <LinkComponent
                                                                onClick={() => {
                                                                    setFieldValue('signature', ''); // Clear only the signature field
                                                                }}
                                                            >
                                                                Remove Signature
                                                            </LinkComponent>
                                                        </div>
                                                    </div>
                                                }

                                                <div className="t-form-actions">
                                                    <ButtonComponent
                                                        variant={'outlined'}
                                                        className={'mrg-right-20'}
                                                        color={'accent'}
                                                        onClick={() => navigate(CommonService._routeConfig.ClientResource())}
                                                    >
                                                        Cancel
                                                    </ButtonComponent>
                                                    <ButtonComponent
                                                        id={"submit_btn"}
                                                        color={'accent'}
                                                        type={"submit"}
                                                        disabled={!isValid || isSubmitting}
                                                        isLoading={isSubmitting}
                                                    >
                                                        Save
                                                    </ButtonComponent>
                                                </div>
                                            </div>
                                        </Form>
                                    )
                                }}
                            </Formik>

                            <ModalComponent isOpen={isSignaturePopupOpen}
                                            onClose={handleSignaturePopupClose}
                                            size={"xl"}
                            >
                                <SignaturePopupComponent
                                    onSign={(signImage: any) => {
                                        formikRef?.current.setFieldValue('signature', signImage)
                                        setAuthorizationToReleaseMedicalInformationFormValues({
                                            ...formikRef?.current?.values,
                                            signature: signImage
                                        })
                                        handleSignaturePopupClose();
                                    }}
                                    handleSignaturePopupClose={handleSignaturePopupClose}
                                />
                            </ModalComponent>
                        </div>
                    </CardComponent>
                </>
            }
        </div>
    )
}
export default ClientAuthorisationToReleaseHealthInfo;
